import { useState, useEffect, useRef, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

function processTimestamps(content: string): string {
  return content.replace(/\[t:([\d.]+)(?:-([\d.]+))?\]/g, (_, start, end) => {
    const startNum = parseFloat(start)
    const formatTs = (s: number) => s < 60 ? `${s.toFixed(1)}s` : `${Math.floor(s/60)}:${Math.floor(s%60).toString().padStart(2,'0')}`
    if (end) {
      const endNum = parseFloat(end)
      return `[${formatTs(startNum)}-${formatTs(endNum)}](timestamp:${startNum}-${endNum})`
    }
    return `[${formatTs(startNum)}](timestamp:${startNum})`
  })
}

interface StreamingTextProps {
  content: string
  className?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  markdownComponents?: Record<string, any>
  // Reveal speed (default 3 chars per animation frame, ~180 chars/s)
  charsPerFrame?: number
  // Fired once when the full content has been revealed (replay mode)
  onComplete?: () => void
}

export function StreamingText({
  content,
  className = '',
  markdownComponents,
  charsPerFrame = 3,
  onComplete,
}: StreamingTextProps) {
  const [displayedLength, setDisplayedLength] = useState(0)
  const animationFrameRef = useRef<number | null>(null)
  const contentLengthRef = useRef(content.length)
  const completedRef = useRef(false)

  contentLengthRef.current = content.length

  const animate = useCallback(() => {
    setDisplayedLength((prev) => {
      const target = contentLengthRef.current
      if (prev >= target) return prev
      return Math.min(prev + charsPerFrame, target)
    })
    animationFrameRef.current = requestAnimationFrame(animate)
  }, [charsPerFrame])

  useEffect(() => {
    if (!onComplete || completedRef.current) return
    if (displayedLength >= content.length) {
      completedRef.current = true
      onComplete()
    }
  }, [displayedLength, content.length, onComplete])

  useEffect(() => {
    animationFrameRef.current = requestAnimationFrame(animate)
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
    }
  }, [animate])

  useEffect(() => {
    if (content.length < displayedLength) {
      setDisplayedLength(0)
    }
  }, [content.length, displayedLength])

  const displayedContent = processTimestamps(content.slice(0, displayedLength))

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
        urlTransform={(url) => url}
      >
        {displayedContent}
      </ReactMarkdown>
    </div>
  )
}
